import axios from 'axios';

// API Base URL - empty string for same-domain deployment (Vercel)
// or set REACT_APP_API_URL for separate backend deployment
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000'
);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and page info
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add current page info for activity logging
    const pathname = window.location.pathname;
    let pageName = 'unknown';
    
    if (pathname === '/dashboard') pageName = 'dashboard';
    else if (pathname === '/users') pageName = 'users';
    else if (pathname === '/appointments') pageName = 'appointments';
    else if (pathname === '/faq') pageName = 'faq';
    else if (pathname === '/profile') pageName = 'profile';
    else if (pathname === '/admin') pageName = 'admin';
    else if (pathname === '/user-relations') pageName = 'user_relations';
    else if (pathname === '/login') pageName = 'login';
    
    config.headers['X-Page'] = pageName;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const responseData = error.response?.data;
      
      // Check if backend sent logout flag
      if (responseData?.logout) {
        // Clear token
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        
        // Show appropriate message
        if (responseData.error === 'Account is deactivated') {
          // Store message to show after redirect
          sessionStorage.setItem('logoutMessage', 'Hesabınız pasif hale getirildi. Lütfen yönetici ile iletişime geçin.');
        } else if (responseData.error === 'Token expired') {
          sessionStorage.setItem('logoutMessage', 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.');
        }
        
        // Redirect to login
        window.location.href = '/login';
      } else {
        // Token expired or invalid without logout flag
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
      logout: () => api.post('/api/auth/logout'),
    profile: () => api.get('/api/auth/profile'),
      changePassword: (passwords) => api.post('/api/auth/change-password', passwords),
    refreshToken: () => api.post('/api/auth/refresh'),
};

export const panelUsersAPI = {
  getProfile: () => api.get('/api/panel-users/profile'),
  updateProfile: (data) => api.put('/api/panel-users/profile', data),
  getUsers: (params) => api.get('/api/panel-users', { params }),
  createUser: (data) => api.post('/api/panel-users', data),
  updateUser: (id, data) => api.put(`/api/panel-users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/panel-users/${id}`),
};

export const visaUsersAPI = {
  getUsers: (params) => api.get('/api/visa-users', { params }),
  getUser: (id) => api.get(`/api/visa-users/${id}`),
  createUser: (data) => api.post('/api/visa-users', data),
  updateUser: (id, data) => api.put(`/api/visa-users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/visa-users/${id}`),
};

export const appointmentsAPI = {
  getAppointments: (params) => api.get('/api/appointments', { params }),
  getAppointment: (id) => api.get(`/api/appointments/${id}`),
  createAppointment: (data) => api.post('/api/appointments', data),
  updateAppointment: (id, data) => api.put(`/api/appointments/${id}`, data),
  deleteAppointment: (id) => api.delete(`/api/appointments/${id}`),
};

export const faqAPI = {
  getFAQs: (params) => api.get('/api/faq', { params }),
  getFAQ: (id) => api.get(`/api/faq/${id}`),
  createFAQ: (data) => api.post('/api/faq', data),
  updateFAQ: (id, data) => api.put(`/api/faq/${id}`, data),
  deleteFAQ: (id) => api.delete(`/api/faq/${id}`),
};

export const adminAPI = {
  getStats: () => api.get('/api/admin/stats'),
  getUsers: (params) => api.get('/api/admin/users', { params }),
  updateUser: (id, data) => api.put(`/api/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  getOffices: () => api.get('/api/admin/offices'),
  createOffice: (data) => api.post('/api/admin/offices', data),
  updateOffice: (id, data) => api.put(`/api/admin/offices/${id}`, data),
  deleteOffice: (id) => api.delete(`/api/admin/offices/${id}`),
};

export default api; 